/** @jsxImportSource @emotion/react */
import { css, Global } from '@emotion/react';
import Cookies from 'js-cookie';
import * as R from 'ramda';
import { Provider } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { applyMiddleware, compose, createStore, Dispatch } from 'redux';
import { apiMiddleware } from 'redux-api-middleware';
import LoginTimer from './auth/login/LoginTimer';
import PrivateRoute from './auth/PrivateRoute';
import CopyToClipboardHost from './copyToClipboard/CopyToClipboardHost';
import AppStateToCookieMapper from './core/cookies/AppStateToCookieMapper';
import { AUTH } from './core/cookies/cookieKeys';
import ModalHost from './core/modal/ModalHost';
import { DEFAULT_STATE } from './core/redux/IAppState';
import { loggingMiddleware } from './core/redux/middleware/loggingMiddleware';
import { noOpMiddleware } from './core/redux/middleware/noOpMiddleware';
import { rootReducer } from './core/redux/rootReducer';
import {
  ACCESS_OVERVIEW,
  ADMIN,
  FAQ,
  GLOBAL,
  GUEST,
  HELP_AND_TRAINING,
  LOGIN_ROUTE,
  MANAGE_USERS,
  PRODUCT,
  PRODUCT_ADMINISTRATION,
  PRODUCT_MAPPING,
  RELEASE_NOTES,
  REPORTS,
  ROOT,
  ROOT_PATH,
  SYSTEM_ADMIN,
  TEST_GROUND_ROUTE,
  VALIDATION_CONFIGURATION,
} from "./core/routes";
import { clientIds, IS_DEV_ENV, IS_TEST_ENV } from "./core/windowProps";
import {LandingPage} from './landingPage/LandingPage';
import TestingPage from './landingPage/TestingPage';
import { isNilOrEmpty } from './language/module';
import { secondsUntil } from './language/time';
import {unregister} from './registerServiceWorker';
import SnackbarHost from './snackbar/SnackbarHost';
import ResortToolsVersions from './version/ResortToolsVersions';
import SeleniumLoadingIndicator from './SeleniumLoadingIndicator';
import {LoginPage} from './auth/login/LoginPage';
import {Auth0Provider} from '@auth0/auth0-react';
import {createRoot} from 'react-dom/client';
import { LandingIndexRoute } from './landingPage/LandingIndexRoute';
import ProductMappingPage from './resortMappingTool/ProductMappingPage';
import { ValidationConfigurationPage } from './validationConfigurationTool/ValidationConfigurationPage';
import ReleaseNotesPage from './appDocs/releaseNotes/ReleaseNotesPage';
import GuestSearchResultsWrapper from './guestSearch/GuestSearchResultsWrapper';
import { UserManagementPage } from './admin/userManagement/UserManagementPage';
import SystemAdminPage from './admin/systemAdmin/SystemAdminPage';
import FaqPage from './appDocs/faq/FaqPage';
import HelpAndTrainingPage from './helpAndTraining/HelpAndTrainingPage';
import TrainingDocumentViewerPage from './helpAndTraining/TrainingDocumentViewerPage';
import { ReportsPage } from './reports/ReportsPage';
import { useEffect } from 'react';
import { AnyAction } from 'redux';
import { ProductAdminLayout } from './productAdministration/ProductAdminLayout';
import { AccessOverviewPage } from './productAdministration/AccessOverviewPage';
import { ProductPage } from './productAdministration/ProductPage';
import { GlobalPage } from './productAdministration/GlobalPage';
import RequireRole from './auth/RequireRole';
import { thunk } from "redux-thunk";
import './styles/animations.css';
import './styles/fonts.css';
import './styles/index.css';

export const GlobalStyles = () => (
  <Global
    styles={css`
      * {
        font-family: Renner, sans-serif !important;
        letter-spacing: 1px; /* Renner is a little too close-packed for people to work in it daily */
      }
    `}
  />
);

// @ts-ignore
const devTools = IS_DEV_ENV && window.__REDUX_DEVTOOLS_EXTENSION__ ? window.__REDUX_DEVTOOLS_EXTENSION__() : f => f;
const logger   = IS_DEV_ENV || IS_TEST_ENV ? loggingMiddleware : noOpMiddleware;


const enhancers = compose(
  applyMiddleware(
    logger, // Keep first to log all incoming actions unaltered
    thunk,
    apiMiddleware,
  ),
  devTools,
);

export function getJsonCookie<T = unknown>(name: string): T | undefined {
  const val = Cookies.get(name);
  if (!val) return undefined;
  try {
    return JSON.parse(val) as T;
  } catch (err) {
    console.warn(`Cookie "${name}" is not valid JSON`, err);
    return undefined;
  }
}

// Initialize app store with login state from cookie so we remember the user was logged in when they refresh
const storedAuth = getJsonCookie<any>(AUTH);
const url = window.location.origin;
const hostname = new URL(url).hostname;
const environment = hostname.split('.')[0] === 'resorttools' ? 'prod' : hostname.split('.')[0];
const authEnv = !IS_DEV_ENV ? environment : 'dev';


const domain = authEnv === 'prod' ? 'auth.resorttools.ikonpass.com' : `${authEnv}.auth.resorttools.ikonpass.com`;
const clientId = clientIds[authEnv];
const resortApiAudience = 'resort-tools-api'
export const redirectUri = IS_DEV_ENV ? 'http://localhost:3000/ResortTools/login' : window.location.origin + '/login';

const authParams = {
  redirect_uri: redirectUri,
  audience:     resortApiAudience,
  scope: 'read:current_user update:current_user_metadata ',
}

let initialAppState = DEFAULT_STATE;

if (storedAuth) {
  storedAuth.isLoggedIn = !isNilOrEmpty(storedAuth.bearerToken) // Has a bearer token
                          && !isNilOrEmpty(storedAuth.bearerTokenExp) // Has an expiration
                          && secondsUntil(storedAuth.bearerTokenExp as string | Date) > 0; // Expires in the future

  if (storedAuth.isLoggedIn) {
    initialAppState = R.mergeDeepRight(DEFAULT_STATE, { auth: storedAuth });
  }
}

export const store = createStore(rootReducer, initialAppState, enhancers);
export type DispatchType = Dispatch<AnyAction>;

const rootContainer = document.getElementById("root");

function DebugRouteLogger() {
  const location = useLocation();
  useEffect(() => {
    console.groupCollapsed(
      `%c🔎 Route change: ${location.pathname}${location.search}${location.hash}`,
      "color:#0b6cff;font-weight:bold;"
    );
    console.log("pathname:", location.pathname);
    console.log("search:", location.search);
    console.log("hash:", location.hash);
    console.log("state:", location.state);
    console.log("root", ROOT);
    console.log("login path", LOGIN_ROUTE);
    console.groupEnd();
  }, [location]);
  return null;
}

rootContainer && createRoot(rootContainer).render(
<>
  <GlobalStyles></GlobalStyles>
  <Auth0Provider
    domain={domain||''}
    clientId={clientId||''}
    authorizationParams={authParams}
    useRefreshTokens={true}
    cacheLocation={'localstorage'}
  >
    <Provider store={store}>
      <SeleniumLoadingIndicator />
      {/*@ts-ignore*/}
      <BrowserRouter>
        <div
          css={css`
        width:    100vw;
        height:   100vh;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      `}
        >
          <Routes>
            {/* Public route*/}
            <Route path={LOGIN_ROUTE} element={<LoginPage />} />
            
            {/* Dev-only route */}
            {
              IS_DEV_ENV && <Route path={TEST_GROUND_ROUTE} element={<TestingPage />} />
            }

            {/* Protected */}
            <Route element={<PrivateRoute />}>  
              <Route element={<RequireRole />}>        
                <Route path={`${ROOT}/`} element={<LandingPage />}>

                  {/* Index under ROOT → ResultsPlaceholder */}
                  <Route index element={<LandingIndexRoute />} />

                  <Route path={PRODUCT_MAPPING} element={<ProductMappingPage />} />
                  <Route path={VALIDATION_CONFIGURATION} element={<ValidationConfigurationPage />} />
                  <Route path={RELEASE_NOTES} element={<ReleaseNotesPage />} />

                  {/* Guest section: /guest/:query */}
                  <Route path={`${GUEST}/:query`} element={<GuestSearchResultsWrapper />} />

                  {/* Admin section: /admin/... */}
                  <Route path={ADMIN}>
                    <Route path={MANAGE_USERS} element={<UserManagementPage />} />
                    <Route path={SYSTEM_ADMIN} element={<SystemAdminPage />} />
                  </Route>

                  {/* product administration sub-routes */}
                  <Route path={PRODUCT_ADMINISTRATION} element={<ProductAdminLayout />} >
                    <Route path={PRODUCT} element={<ProductPage />} />
                    <Route path={ACCESS_OVERVIEW} element={<AccessOverviewPage />} />
                    <Route path={GLOBAL} element={<GlobalPage />} />
                  </Route>

                  {/* Other top-level children under ROOT */}
                  <Route path={FAQ} element={<FaqPage />} />
                  <Route path={HELP_AND_TRAINING}>
                    <Route index element={<HelpAndTrainingPage />} />
                    <Route path=":documentId" element={<TrainingDocumentViewerPage />} />
                  </Route>
                  <Route path={REPORTS} element={<ReportsPage />} />
                </Route>
              </Route>
            </Route> 
            {/* Catch-all redirect to root */}  
            <Route path="*" element={<Navigate to={ROOT_PATH} replace />} />
          </Routes>

          <SnackbarHost />
          <ResortToolsVersions />
          <ModalHost />
          {/* Non-rendering */}
          <AppStateToCookieMapper /> {/* Listens for changes in state and maps those changes to cookies when we want */}
          <CopyToClipboardHost /> {/* Listens for an action to copy text to the system's clipboard */}
          <LoginTimer /> {/* Keeps track of timing and schedules logouts for session and inactivity */}
        </div>
      </BrowserRouter>
    </Provider>
  </Auth0Provider>
  </>
);

// Worker that checks for updated versions of the app and refreshes
// registerServiceWorker();
unregister();
